import React, { useEffect, useState } from 'react'
import { Button, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListUl } from '@fortawesome/pro-solid-svg-icons/faListUl'
import { getAreasData } from 'helpers'
import type { IArea } from '../../interfaces/areas'
import { AreasModalOpenMode } from '../../interfaces/areaModalProps'
import type { IAreasModal } from '../../interfaces/areaModalProps'
import AreasModal from './AreasModal.component'
import translationsTemplate from '../../constants/translations'
import { isObject, mergeDeep } from '../../helpers/mergeDeep'
import styles from './AreasSelector.module.scss'

interface ModalContentProps {
  areasArray: IArea[]
  loading: boolean
  currentArea: IArea
  translations: any
  handleShowAreasModal: (show: boolean) => void
}

const ModalContent: React.FC<ModalContentProps> = ({
  areasArray,
  loading,
  currentArea,
  translations,
  handleShowAreasModal,
}) =>
  areasArray.length > 0 ? (
    <Button
      className={styles.filterSelect}
      loading={loading}
      onClick={() => {
        handleShowAreasModal(true)
      }}
    >
      {!loading && currentArea.name} <FontAwesomeIcon icon={faListUl} />
    </Button>
  ) : (
    <Tooltip placement="top" title={translations.notAvailable}>
      <div>
        <Button
          disabled
          className={styles.filterSelect}
          loading={loading}
          onClick={() =>
            !(areasArray.length === 0) && handleShowAreasModal(true)
          }
        >
          {!loading && currentArea.name} <FontAwesomeIcon icon={faListUl} />
        </Button>
      </div>
    </Tooltip>
  )

export const AreasSelector: React.FC<IAreasModal> = ({
  mandatoryArea = true,
  selectedArea,
  areas,
  onAreaSelected,
  locale,
  loading = false,
  open = false,
  onSetShowAreasModal,
  mode = AreasModalOpenMode.Default,
}) => {
  const [showAreasModal, setShowAreasModal] = useState(false)
  const [currentArea, setCurrentArea] = useState({} as IArea)
  const [areasArray, setAreasArray] = useState([] as IArea[])
  const [translations, setTranslations] = useState(
    translationsTemplate.modal.areas,
  )

  useEffect(() => {
    const auxData = areas.map((area) => ({ ...area })) // Deepclone de bajo presupuesto
    const allTheCompany = [{ id: 0, name: translations.allTheCompany }]

    if (areasArray.length === 0 && !(auxData.length === 0)) {
      setAreasArray(mandatoryArea ? auxData : [...allTheCompany, ...auxData])
    } else {
      const notEqual = auxData.find(
        ({ id }: IArea, index: number) =>
          areasArray[index] && areasArray[index].id === id,
      )
      notEqual &&
        setAreasArray(mandatoryArea ? auxData : [...allTheCompany, ...auxData])
    }
  }, [areas])

  useEffect(() => {
    isObject(locale) &&
      Object.keys(locale).length > 0 &&
      setTranslations(mergeDeep(locale, translations))
  }, [locale])

  useEffect(() => {
    setShowAreasModal(open)
  }, [open])

  useEffect(() => {
    if (
      selectedArea &&
      !isNaN(selectedArea.id) &&
      selectedArea.id !== currentArea.id
    ) {
      setCurrentArea(selectedArea)
    } else if ((!selectedArea || isNaN(selectedArea.id)) && !!areasArray[0]) {
      const selected = areasArray[0]
      const hierarchy = getAreasData(
        selected.parentId,
        [selected],
        areasArray,
      ).reverse()
      setCurrentArea(selected)
      onAreaSelected({
        ...selected,
        successorHierarchy: getSuccessorHierarchy(areasArray[0].id),
        hierarchy,
        trigger: 'on_not_current_selected_area',
      })
    }
  }, [selectedArea, areasArray])

  const handleAreaSelected = (area: IArea) => {
    const hierarchy = getAreasData(area.parentId, [area], areasArray).reverse()
    onAreaSelected({
      ...area,
      successorHierarchy: getSuccessorHierarchy(area.id),
      hierarchy,
      trigger: 'on_area_selected',
    })
    setCurrentArea(area)
  }

  const getSuccessorHierarchy = (id: number) => {
    const result = [] as number[]
    const getSuccessor = (id: number) => {
      if (id >= 0) {
        result.push(id)
        const childrens = areas.filter(({ parentId }: IArea) => parentId === id)
        childrens.map(({ id }: IArea) => {
          getSuccessor(id)
        })
      }
    }
    getSuccessor(id)
    return result
  }

  const handleShowAreasModal = (state: boolean) => {
    setShowAreasModal(state)
    onSetShowAreasModal?.(state)
  }

  return (
    <div className={`${styles.areasSelector} rankmiTable-areas`}>
      {mode === AreasModalOpenMode.Default ? (
        <ModalContent
          areasArray={areasArray}
          loading={loading}
          currentArea={currentArea}
          handleShowAreasModal={handleShowAreasModal}
          translations={translations}
        />
      ) : null}
      {showAreasModal && (
        <AreasModal
          areas={areasArray}
          loading={loading}
          selected={currentArea}
          show={showAreasModal}
          translations={translations}
          onAreaSelected={handleAreaSelected}
          onClose={() => {
            handleShowAreasModal(false)
          }}
        />
      )}
    </div>
  )
}
